<!-- Shared template for header content -->
<ng-template #headerTopContent let-isSticky="isSticky">
  <div class="header__nav">
    <!-- Logo: links back to previous view, shows back arrow on hover only in article mode -->
    <a
      class="header__logo header__logo--clickable"
      [class.header__logo--with-back]="mode() === 'article'"
      [routerLink]="stateService.backRoute()"
      [queryParams]="stateService.backQueryParams()"
    >
      <img
        src="assets/tellent-updates-icon.png"
        alt="Tellent Product Updates"
        class="header__logo-img"
      />
      <rt-icon name="keyboard-arrow-left" class="header__logo-back-icon" />
    </a>

    <!-- Mobile: show active tab label next to logo -->
    @if (mode() === 'feed') {
      <span class="header__active-tab-label">
        {{ getActiveMainTabLabel() }}
        @if (activeMainTab() === 'timeline' && stateService.timelineQuarterLabel()) {
          <rt-icon name="keyboard-arrow-right" class="header__breadcrumb-chevron" />
          <span class="header__breadcrumb-item">{{ stateService.timelineQuarterLabel() }}</span>
          <rt-icon name="keyboard-arrow-right" class="header__breadcrumb-chevron" />
          <span class="header__breadcrumb-item header__breadcrumb-item--current">{{
            stateService.timelineMonthLabel()
          }}</span>
        }
      </span>
    }

    @if (mode() === 'article') {
      @if (article()) {
        <!-- Article mode: show badges and title -->
        <div class="header__article-info">
          <div class="header__article-badges">
            @for (category of article()!.updateCategories; track category) {
              <pu-category-badge [category]="category" type="update" variant="ghost" />
            }
            @for (category of article()!.productCategories; track category) {
              <pu-category-badge [category]="category" type="product" variant="ghost" />
            }
          </div>
          <rt-heading variant="heading large-xx bold" class="header__article-title">
            {{ primaryTranslation()!.title }}
          </rt-heading>
        </div>
      } @else {
        <!-- Article mode loading: show skeleton -->
        <div class="header__article-info header__article-info--loading">
          <div class="header__article-badges">
            <rt-placeloader class="header__badge-skeleton" variant="text" [short]="true" />
            <rt-placeloader class="header__badge-skeleton" variant="text" [short]="true" />
          </div>
          <rt-placeloader class="header__title-skeleton" variant="text" />
        </div>
      }
    } @else {
      <!-- Feed mode: show navigation tabs -->
      <nav class="header__main-nav">
        <a
          class="header__main-nav-item"
          [class.header__main-nav-item--active]="activeMainTab() === 'whats-new'"
          [attr.data-text]="translationService.translate('nav.whats_new')"
          (click)="onMainTabChange('whats-new')"
        >
          {{ translationService.translate('nav.whats_new') }}
        </a>
        <a
          class="header__main-nav-item"
          [class.header__main-nav-item--active]="activeMainTab() === 'coming-soon'"
          [attr.data-text]="translationService.translate('nav.coming_soon')"
          (click)="onMainTabChange('coming-soon')"
        >
          {{ translationService.translate('nav.coming_soon') }}
        </a>
        <a
          class="header__main-nav-item"
          [class.header__main-nav-item--active]="activeMainTab() === 'timeline'"
          [attr.data-text]="translationService.translate('nav.timeline')"
          (click)="onMainTabChange('timeline')"
        >
          {{ translationService.translate('nav.timeline') }}
        </a>
        <a
          class="header__main-nav-item"
          [class.header__main-nav-item--active]="activeMainTab() === 'changelog'"
          [attr.data-text]="translationService.translate('nav.changelog')"
          (click)="onMainTabChange('changelog')"
        >
          {{ translationService.translate('nav.changelog') }}
        </a>
      </nav>
    }
  </div>

  <div class="header__actions">
    <!-- Hamburger menu button (mobile only) -->
    <button
      class="header__hamburger"
      (click)="toggleMobileMenu()"
      [attr.aria-expanded]="isMobileMenuOpen()"
      [attr.aria-label]="translationService.translate('accessibility.open_navigation_menu')"
    >
      <rt-icon name="tellent-nav-hamburger-inactive" />
    </button>

    <rt-icon-button
      size="small"
      variant="plain-ghost"
      [iconName]="isDarkMode() ? 'dark' : 'light'"
      (click)="toggleTheme()"
      [attr.aria-label]="translationService.translate('accessibility.toggle_theme')"
    />

    <div class="header__language-dropdown">
      <rt-button
        variant="plain-ghost"
        size="small"
        class="header__language-button"
        [class.header__language-button--open]="isLanguageDropdownOpen()"
        (click)="toggleLanguageDropdown()"
      >
        {{ getLanguageLabel(stateService.currentLocale()) }}
        <rt-icon name="keyboard-arrow-down" class="header__language-chevron" />
      </rt-button>

      @if (isLanguageDropdownOpen()) {
        <div class="header__language-menu">
          @for (locale of stateService.availableLocales(); track locale) {
            <button
              class="header__language-option"
              [class.header__language-option--active]="locale === stateService.currentLocale()"
              (click)="selectLanguage(locale)"
            >
              {{ getLanguageLabel(locale) }}
            </button>
          }
        </div>
      }
    </div>
  </div>
</ng-template>

<!-- Normal header (always in document flow) -->
<header class="header" [class.header--article]="mode() === 'article'">
  <div class="header__glass">
    <div class="header__inner">
      <div class="header__top">
        <ng-container *ngTemplateOutlet="headerTopContent; context: { isSticky: false }" />
      </div>

      @if (mode() === 'feed') {
        <!-- Feed mode: show product tabs -->
        <div class="header__product-tabs-wrapper">
          <nav class="header__product-tabs">
            @for (tab of productTabs; track tab) {
              <button
                class="header__product-tab"
                [class.header__product-tab--active]="activeProductTab() === tab"
                (click)="onProductTabChange(tab)"
              >
                <span class="header__product-tab-label">{{ getProductTabLabel(tab) }}</span>
                <div class="header__product-tab-indicator"></div>
              </button>
              <!-- Dot separator to group Hire/Manage/Grow -->
              @if (tab === 'ai') {
                <span class="header__product-tab-separator">·</span>
              }
            }

            <a
              class="header__feedback"
              href="https://feedback.tellent.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <rt-button variant="plain-ghost" size="small">
                {{ translationService.translate('header.give_feedback') }}
              </rt-button>
            </a>
          </nav>
        </div>
      }
    </div>
  </div>
</header>

<!-- Sticky header (fixed, animated) - only for feed mode -->
@if (mode() === 'feed') {
  <header
    class="header header--sticky"
    [ngClass]="{
      'header--sticky-visible': stickyState() === 'visible',
      'header--sticky-hiding': stickyState() === 'hiding',
    }"
  >
    <div class="header__glass">
      <div class="header__inner">
        <div class="header__top">
          <ng-container *ngTemplateOutlet="headerTopContent; context: { isSticky: true }" />
        </div>
      </div>
    </div>
  </header>
}

<!-- Mobile navigation menu -->
@if (isMobileMenuOpen()) {
  <pu-mobile-menu
    [isOpen]="isMobileMenuOpen()"
    [activeMainTab]="activeMainTab()"
    [activeProductTab]="activeProductTab()"
    [currentLocale]="stateService.currentLocale()"
    [availableLocales]="stateService.availableLocales()"
    [isDarkMode]="isDarkMode()"
    (closeMenu)="closeMobileMenu()"
    (mainTabChange)="onMobileMainTabChange($event)"
    (productTabChange)="onMobileProductTabChange($event)"
    (localeChange)="selectLanguage($event)"
    (themeToggle)="toggleTheme()"
  />
}
